import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PublicApiService } from '../../services/public-api.service';
import { Fleet } from '../../models/fleet.model';
import { translatePlaceToLocation } from '../../lib/helpers';

declare var bootstrap: any;
declare var google: any;

@Component({
  selector: 'app-add-location',
  templateUrl: './add-location.component.html',
  styleUrls: ['./add-location.component.scss']
})
export class AddLocationComponent implements OnInit {
  @Input() selectedFleet: Fleet | null = null;
  @Output() locationAdded = new EventEmitter<void>();

  // New location
  newLocationName: string = '';
  selectedPlace: any = null;
  newLocation: any = null;

  // Badge status
  locationStatusText: string = '';
  locationStatusTheme: string = 'light';

  // Submit button status
  submitButtonEnabled: boolean = false;

  constructor(private publicApiService: PublicApiService) {}

  ngOnInit(): void {}

  /**
   * Uses the Google Maps Geocoding API to get detailed geocode information for the given address
   * @param address The address to geocode
   * @returns A promise that resolves to an array of geocoded results
   */
  async geocodeByAddress(address: string): Promise<any[]> {
    return new Promise((resolve, reject) => {
      // Ensure the Google Maps API is loaded
      if (!google || !google.maps || !google.maps.Geocoder) {
        console.error('Google Maps API not loaded');
        reject('Google Maps API not loaded');
        return;
      }

      // Create a new Geocoder instance
      const geocoder = new google.maps.Geocoder();

      // Call the geocode method with the address
      geocoder.geocode({ address }, (results: any[], status: any) => {
        if (status === google.maps.GeocoderStatus.OK) {
          resolve(results);
        } else {
          console.error('Geocoding failed:', status);
          reject(status);
        }
      });
    });
  }

  openAddLocationModal() {
    // Reset the new location name and selected place
    this.newLocationName = '';
    this.selectedPlace = null;

    // Initialize badge status to light theme
    this.locationStatusText = '';
    this.locationStatusTheme = 'light';

    // Initialize submit button as disabled
    this.submitButtonEnabled = false;

    // Get the modal element
    const modalElement = document.getElementById('addLocationModal');

    // Create and show the modal
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  async onPlaceSelected(place: any) {

    this.selectedPlace = place;
    console.log('Selected place:', place);

    this.newLocationName = '';

    // Update badge to 'checking' with 'info' theme
    this.locationStatusText = 'checking';
    this.locationStatusTheme = 'info';
    this.submitButtonEnabled = false;

    this.newLocation = translatePlaceToLocation(this.selectedFleet, place);

    if(!this.newLocation){
      this.newLocationName = '';
      this.locationStatusText = 'No location details found. Be sure to select a location from the list.';
      this.locationStatusTheme = 'danger';
      this.submitButtonEnabled = false;
      return;
    }

    const exists = await this.publicApiService.checkLocationExists(this.newLocation.label);

    // Update badge based on whether the location exists
    if (exists) {
      this.locationStatusText = 'Location Already Exists';
      this.locationStatusTheme = 'danger';
      this.submitButtonEnabled = false;
    } else {
      this.newLocationName = this.newLocation.label;
      this.locationStatusText = 'Ready to add location';
      this.locationStatusTheme = 'success';
      this.submitButtonEnabled = true;
    }

  }

  async submitNewLocation() {
    if (!this.newLocationName || !this.selectedFleet) {
      return;
    }

    console.log('Adding new location:', this.newLocationName);

    if (this.newLocation) {
      console.log('With location details:', this.newLocation);

      try {
        const response = await this.publicApiService.createLocation(this.newLocation);
        if(!response) {
          this.locationStatusText = 'Failed to add location. Please try again.';
          this.locationStatusTheme = 'danger';
          this.submitButtonEnabled = false;
          return;
        }
      } catch(e:any){
        console.error('Error adding location:', e);
        console.error('Error adding location:', e.error.message || e.message || e.toString() || JSON.stringify(e) || 'Unknown error');
        this.locationStatusText = 'Failed to add location. Please try again.';
        this.locationStatusTheme = 'danger';
        this.submitButtonEnabled = false;
        return;
      }

    }

    // Close the modal
    const modalElement = document.getElementById('addLocationModal');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }

    // Reset the new location name, selected place, new location, badge status, and submit button
    this.newLocationName = '';
    this.selectedPlace = null;
    this.newLocation = null;
    this.locationStatusText = '';
    this.locationStatusTheme = 'light';
    this.submitButtonEnabled = false;

    // Emit event to notify the parent component that a location was added
    this.locationAdded.emit();
  }

  getSelectedFleetName(): string {
    return this.selectedFleet?.id || '';
  }
}
